import { useEffect, useMemo, useRef } from "react"
import { map, isEmpty } from "lodash"
import { Creators as NotificationCreators } from "modules/notifications/model"
import { LOADING, OK } from "helpers/data-statuses"
import { useIntradayPriceStream } from "connected-components/dashboard/hooks/use-intraday-price"
import { useIntradayStandalonePerformanceStream } from "connected-components/dashboard/hooks/use-intraday-standalone-performance"
import {
  buildIntradayFlatMap,
  buildIntradayStandaloneFlatMap,
} from "gatherers/contributors/intraday"
import { collectLeafModelProviderIds } from "helpers/contributors"
import { enrichMetricsWithIntradayAsOfTooltips } from "helpers/metrics"
import {
  buildContributionSuffixIndexByModelProviderId,
  getInstrumentSuffixForIntradayContributionPrefix,
  getIntradayContributionAutoRefreshWarningState,
  mergeFlatContributors,
  normalizeContributorKey,
  shouldAutoInvalidateIntradayContributionGathererOnPricingUpdate,
  shouldSubscribeToIntradayPriceTicks,
} from "../contributors-data-integrator.utils"

const EMPTY_ARRAY = []
const INTRADAY_CONTRIBUTION_AUTO_REFRESH_RANGE_WARNING = "The date range selected exceeds the supported range (1M) for automatic intraday contribution performance refreshes. Select 1M or less for intraday contribution."

function getIntradayContributionGathererModelId(gatherer) {
  return gatherer?.modelId || gatherer?.variables?.modelId
}

export function useIntradayContributorsEnhancement({
  nonIntradayGatherers,
  intradayGatherers,
  metrics,
  data,
  status,
  intradayContributionPerformanceLastUpdatedAt,
  isFetchingIntradayContributionQueries,
  isLoadingNonIntradayContributionQueries,
  isIntradayAnalyticsEnabled,
  instrumentsWithMetadata,
  queryClient,
  dispatch,
}) {
  const hasShownIntradayContributionAutoRefreshWarningRef = useRef(false)
  const intradayContributionQueryGatherers = useMemo(() => {
    return nonIntradayGatherers.filter((gatherer) =>
      getInstrumentSuffixForIntradayContributionPrefix(gatherer.prefix) != null,
    )
  }, [nonIntradayGatherers])
  const hasIntradayStreamingOrContributionGatherers = intradayGatherers.length > 0 || intradayContributionQueryGatherers.length > 0
  const intradayPriceGatherers = useMemo(() => {
    return intradayGatherers.filter((gatherer) => gatherer.streamType === "prices")
  }, [intradayGatherers])
  const intradayStandaloneGatherers = useMemo(() => {
    return intradayGatherers.filter((gatherer) => gatherer.streamType === "standalone-performance")
  }, [intradayGatherers])

  const modelProviderIds = useMemo(() => {
    if (!isIntradayAnalyticsEnabled || !hasIntradayStreamingOrContributionGatherers || !data || !data.children) {
      return EMPTY_ARRAY
    }
    return collectLeafModelProviderIds(data)
  }, [data, isIntradayAnalyticsEnabled, hasIntradayStreamingOrContributionGatherers])

  const shouldEnableIntraday =
    isIntradayAnalyticsEnabled
    && hasIntradayStreamingOrContributionGatherers
    && !!data
    && modelProviderIds.length > 0
  const shouldSubscribeToPriceStream = shouldSubscribeToIntradayPriceTicks({
    intradayPriceGatherers,
    intradayContributionQueryGatherers,
  })
  const intradayStreamModelId =
    intradayPriceGatherers[0]?.modelId
    || getIntradayContributionGathererModelId(intradayContributionQueryGatherers[0])
    || intradayStandaloneGatherers[0]?.modelId

  const {
    prices: intradayPrices,
    lastUpdatedAt: intradayPricesLastUpdatedAt,
    updatedModelProviderIds: intradayUpdatedModelProviderIds,
  } = useIntradayPriceStream({
    modelProviderIds,
    modelId: intradayStreamModelId,
    enabled: shouldEnableIntraday && shouldSubscribeToPriceStream,
  })

  const standaloneSubscriptions = useMemo(() => {
    if (!shouldEnableIntraday || intradayStandaloneGatherers.length === 0) {
      return []
    }
    const periods = [...new Set(intradayStandaloneGatherers.map((gatherer) => gatherer.period).filter(Boolean))]
    return periods.map((period) => ({
      period,
      modelProviderIds,
    }))
  }, [intradayStandaloneGatherers, modelProviderIds, shouldEnableIntraday])

  const { standalonePerformanceByPeriod, lastUpdatedAt: standalonePerformanceLastUpdatedAt } = useIntradayStandalonePerformanceStream({
    subscriptions: standaloneSubscriptions,
    modelId: intradayStandaloneGatherers[0]?.modelId,
    enabled: shouldEnableIntraday && standaloneSubscriptions.length > 0,
  })

  const contributionQueryGatherersBySuffix = useMemo(() => {
    return intradayContributionQueryGatherers.reduce((gatherersBySuffix, gatherer) => {
      if (!shouldAutoInvalidateIntradayContributionGathererOnPricingUpdate(gatherer)) {
        return gatherersBySuffix
      }

      const suffix = getInstrumentSuffixForIntradayContributionPrefix(gatherer.prefix)
      if (suffix == null) {
        return gatherersBySuffix
      }

      if (!gatherersBySuffix[suffix]) {
        gatherersBySuffix[suffix] = []
      }
      gatherersBySuffix[suffix].push(gatherer)
      return gatherersBySuffix
    }, {})
  }, [intradayContributionQueryGatherers])
  const hasIntradayContributionGatherers = intradayContributionQueryGatherers.length > 0
  const shouldAutoRefreshIntradayContributionOnPricingUpdates = useMemo(() => {
    if (!hasIntradayContributionGatherers) {
      return false
    }
    return intradayContributionQueryGatherers.some(shouldAutoInvalidateIntradayContributionGathererOnPricingUpdate)
  }, [hasIntradayContributionGatherers, intradayContributionQueryGatherers])

  const contributionSuffixIndexByModelProviderId = useMemo(() => {
    return buildContributionSuffixIndexByModelProviderId(data)
  }, [data])

  useEffect(() => {
    const {
      shouldShowWarning,
      nextHasShownWarning,
    } = getIntradayContributionAutoRefreshWarningState({
      hasIntradayContributionGatherers,
      shouldAutoRefreshIntradayContributionOnPricingUpdates,
      hasShownWarning: hasShownIntradayContributionAutoRefreshWarningRef.current,
    })

    hasShownIntradayContributionAutoRefreshWarningRef.current = nextHasShownWarning
    if (!shouldShowWarning) {
      return
    }

    dispatch(NotificationCreators.add("warning", null, INTRADAY_CONTRIBUTION_AUTO_REFRESH_RANGE_WARNING))
  }, [
    dispatch,
    hasIntradayContributionGatherers,
    shouldAutoRefreshIntradayContributionOnPricingUpdates,
  ])

  useEffect(() => {
    if (!shouldEnableIntraday) {
      return
    }
    if (!intradayContributionQueryGatherers.length || !intradayUpdatedModelProviderIds.length) {
      return
    }

    const allSuffixes = Object.keys(contributionQueryGatherersBySuffix)
    if (!allSuffixes.length) {
      return
    }

    const impactedSuffixes = new Set()
    for (const modelProviderId of intradayUpdatedModelProviderIds) {
      const normalizedModelProviderId = normalizeContributorKey(modelProviderId)
      const suffixes = contributionSuffixIndexByModelProviderId.get(modelProviderId)
        || contributionSuffixIndexByModelProviderId.get(normalizedModelProviderId)
      if (suffixes && suffixes.size > 0) {
        suffixes.forEach((suffix) => impactedSuffixes.add(suffix))
      }
    }

    const suffixesToInvalidate = impactedSuffixes.size > 0 ? [...impactedSuffixes] : allSuffixes
    const gatherersToInvalidate = suffixesToInvalidate.flatMap((suffix) => contributionQueryGatherersBySuffix[suffix] || EMPTY_ARRAY)
    if (!gatherersToInvalidate.length) {
      return
    }

    gatherersToInvalidate.forEach((gatherer) => {
      queryClient.invalidateQueries({
        queryKey: [gatherer.key],
        exact: true,
        refetchType: "active",
      })
    })
  }, [
    shouldEnableIntraday,
    intradayContributionQueryGatherers,
    intradayUpdatedModelProviderIds,
    contributionSuffixIndexByModelProviderId,
    contributionQueryGatherersBySuffix,
    queryClient,
  ])

  const dataWithIntraday = useMemo(() => {
    if (!isIntradayAnalyticsEnabled || !hasIntradayStreamingOrContributionGatherers || !data) {
      return data
    }
    if (isEmpty(intradayPrices) && isEmpty(standalonePerformanceByPeriod)) {
      return data
    }

    const intradayPricesByRowId = buildIntradayFlatMap(intradayPrices)
    const priceContributors = map(intradayPriceGatherers, (gatherer) => ({
      prefix: gatherer.prefix,
      valuesByRowId: intradayPricesByRowId,
    }))

    const standaloneFlatMapByPeriodKey = intradayStandaloneGatherers.reduce((flatMapCacheByPeriod, gatherer) => {
      const periodKey = String(gatherer.period)
      if (!flatMapCacheByPeriod.has(periodKey)) {
        flatMapCacheByPeriod.set(
          periodKey,
          buildIntradayStandaloneFlatMap(standalonePerformanceByPeriod, gatherer.period),
        )
      }
      return flatMapCacheByPeriod
    }, new Map())

    const standaloneContributors = map(intradayStandaloneGatherers, (gatherer) => ({
      prefix: gatherer.prefix,
      valuesByRowId: standaloneFlatMapByPeriodKey.get(String(gatherer.period)) || {},
    }))

    return mergeFlatContributors(data, [...priceContributors, ...standaloneContributors])
  }, [
    data,
    intradayPrices,
    intradayPriceGatherers,
    intradayStandaloneGatherers,
    standalonePerformanceByPeriod,
    isIntradayAnalyticsEnabled,
    hasIntradayStreamingOrContributionGatherers,
  ])

  const effectiveStatus = useMemo(() => {
    if (
      status === LOADING
      && isFetchingIntradayContributionQueries
      && !isLoadingNonIntradayContributionQueries
      && dataWithIntraday
    ) {
      return OK
    }
    return status
  }, [status, isFetchingIntradayContributionQueries, isLoadingNonIntradayContributionQueries, dataWithIntraday])

  const metricsWithIntradayTooltip = useMemo(() => {
    if (!isIntradayAnalyticsEnabled || !hasIntradayStreamingOrContributionGatherers) {
      return metrics
    }

    return enrichMetricsWithIntradayAsOfTooltips(metrics, {
      intradayPricesLastUpdatedAt,
      intradayContributionPerformanceLastUpdatedAt,
      intradayStandalonePerformanceLastUpdatedAt: standalonePerformanceLastUpdatedAt,
    }, { instrumentsWithMetadata })
  }, [
    metrics,
    intradayPricesLastUpdatedAt,
    intradayContributionPerformanceLastUpdatedAt,
    standalonePerformanceLastUpdatedAt,
    isIntradayAnalyticsEnabled,
    hasIntradayStreamingOrContributionGatherers,
    instrumentsWithMetadata,
  ])

  return {
    data: dataWithIntraday,
    status: effectiveStatus,
    metrics: metricsWithIntradayTooltip,
    modelProviderIds,
    isIntradayContributionRefreshing: isFetchingIntradayContributionQueries && effectiveStatus === OK,
  }
}
